import React, { FC, useRef, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Route, Switch, useLocation, useHistory } from 'react-router-dom';
import { format } from 'date-fns';
import { useMediaQuery } from '@mui/material';
import './PaymenHistory.scss';
import { AccountTypes, GlobalRouteNames, LocalStorageItems } from '../../models/Common';
import { Payment, BonusesItem } from '../../models/PaymentHistory';
import { YearSelect } from '../../components/paymentHistoryParts/YearSelect';
import { AccountTypeSelect } from '../../components/paymentHistoryParts/AccountTypeSelect';
import { AccountAddressSelect } from '../../components/paymentHistoryParts/AccountAddressSelect';
import { Page404 } from '../404';
import { GetPaymentHistoryProps, paymentsApi } from '../../services/apiServices/user/payments';
import { userInfoApi } from '../../services/apiServices/user/userInfo';
import { PaginationTable } from '../../components/commons/customTables/PaginationTable';
import { CustomTab } from '../../components/commons/CustomTab';
import { TabPanel } from '../../components/commons/CustomTab/TabPanel';
import { BonusesCell } from '../../components/paymentHistoryParts/BonusesCell';
import { paymentHistoryTabs } from './PaymentHistoryTabs';
import { TableBodyCell } from '../../models/CustomTable';
import { MobileCell } from '../../components/paymentHistoryParts/MobileCell';
import { useAppSelector } from '../../hooks/redux';
import { useDateFsnLocale } from '../../hooks/useDateFsnLocale';
import { PaymentHistoryDetails } from '../../components/paymentHistoryParts/PaymentDetails';
import { EmptyNotification } from '../../components/paymentHistoryParts/EmptyNotification';
import { CustomSkeletonElement } from '../../components/commons/CustomSkeletonElement';
import { CustomNumberFormat } from '../../components/commons/CustomNumberFormat';
import { useCustomSnackbar } from '../../hooks/useCustomEnqueueSnackbar';
import { currentYear, firstPaymentHistoryYear } from '../../constants/configuration';
import { getErrorMessage } from '../../utils';
import DateVO from '../../core/valueObjects/DateVO';
import { DateVOv2 } from '../../core/DateVOv2';

export const PaymentHistory: FC = () => {
  const { t, i18n } = useTranslation();
  const locale = i18n.language === 'ua' ? 'uk' : i18n.language;
  const { search } = useLocation();
  const searchParams = new URLSearchParams(search);
  const controlledAccount = localStorage.getItem(LocalStorageItems.ControlledAccount);
  const { useGetUserInfoQuery, useGetUserInfoMultiAccQuery } = userInfoApi;
  const { data: userInfoState, isSuccess: isUserInfoDownloaded } = controlledAccount
    ? useGetUserInfoMultiAccQuery(
        { subAccountId: JSON.parse(controlledAccount).UserID },
        { refetchOnMountOrArgChange: true },
      )
    : useGetUserInfoQuery('', { refetchOnMountOrArgChange: true });
  const getPaymentsPeriod = (params: URLSearchParams) => {
    const startYear = new Date(userInfoState?.CreateDate || '').getFullYear() || 0;
    const firstYear = firstPaymentHistoryYear > startYear ? firstPaymentHistoryYear : startYear;
    if (
      params.has('period') &&
      params.get('period')?.trim().length === 4 &&
      Number(params.get('period')?.trim()) &&
      Number.isInteger(Number(params.get('period')?.trim())) &&
      Number(params.get('period')?.trim()) >= firstYear &&
      Number(params.get('period')?.trim()) <= currentYear
    ) {
      return params.get('period')?.trim();
    }
    return undefined;
  };
  const [tabValue, setTabValue] = useState<number>(0);
  const [touched, setTouched] = useState<boolean>(false);
  const period = getPaymentsPeriod(searchParams);
  const [paymentsYearsOptions, setPaymentsYearsOptions] = useState<string>(`${period ?? currentYear}`);
  const [bonusesYearsOptions, setBonusesYearsOptions] = useState<string>(`${currentYear}`);
  const [serviceType, setServiceType] = useState<AccountTypes | 'all services'>('all services');
  const [subAccount, setSubAccount] = useState<number>(0);
  const [paymentsQueryParams, setPaymentsQueryParams] = useState<GetPaymentHistoryProps>({
    period: `${period ?? currentYear}`,
  });
  const [bonusesQueryParams, setBonusesQueryParams] = useState<{ period?: string }>({ period: `${currentYear}` });
  const [paymentsPage, setPaymentsPage] = useState(0);
  const [bonusesPage, setBonusesPage] = useState(0);
  const {
    data: paymentHistoryApi,
    isFetching,
    isError,
    error,
  } = paymentsApi.useGetPaymentStatisticsQuery(paymentsQueryParams, { refetchOnMountOrArgChange: true });
  const {
    data: paymentBonusesApi,
    isFetching: isLoadingBonuses,
    isError: isBonusError,
    error: bonusesError,
  } = paymentsApi.useGetPaymentBonusesQuery(bonusesQueryParams, {
    refetchOnMountOrArgChange: true,
    skip: !tabValue && !touched,
  });
  const isLoading = !isUserInfoDownloaded || isFetching;
  const isAccountTypeSelector = false;
  const isTabsAvailable = false;
  const isMobile = useMediaQuery('(max-width:767px)');
  const scrollToTop = useRef<HTMLDivElement | null>(null);
  const { settings } = useAppSelector((state) => state.appReducer);
  const { selectedLanguage } = useAppSelector((state) => state.langReducer);
  const { fnsLocale } = useDateFsnLocale(selectedLanguage);
  const { customEnqueueSnackbar } = useCustomSnackbar();
  const expireDate = format(DateVO.create(paymentBonusesApi?.expireDate ?? '').get(true), 'd MMMM yyyy', {
    locale: fnsLocale,
  });
  const {
    data: accountInfo,
    isError: isAccountError,
    error: accountError,
  } = userInfoApi.useGetMultiaccountMembersQuery(selectedLanguage, {
    refetchOnMountOrArgChange: true,
    skip: !settings?.feature?.multiaccount,
  });
  const history = useHistory();

  const tableHead = [t('paymentHistory.payOperation'), t('paymentHistory.date'), t('paymentHistory.amount')];

  const mobileTableHead = [
    <div className="mobile-head">
      <p>{t('paymentHistory.payOperation')}</p>
      <p>{t('paymentHistory.amount')}</p>
    </div>,
  ];

  useEffect(() => {
    if (isAccountError && accountError) {
      const message = getErrorMessage(accountError);
      customEnqueueSnackbar(message, 'error');
    }
  }, [isAccountError, accountError]);

  const handleSetTabValue = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    setTouched(true);
  };

  const getPaymentBonusesCells = (el: BonusesItem) => {
    const cell = [
      <BonusesCell element={el} />,
      ...(!isMobile
        ? [
            <h5 className="light">
              {new DateVOv2({ format: 'yyyy-MM-dd HH:mm:ss', dateString: el.CreateDate }).getDateString(locale)}
            </h5>,
          ]
        : []),
      ...(!isMobile
        ? [
            <div className="payment-details payment-bonus-details">
              <p>
                {el.Amount > 0 && '+'}
                <CustomNumberFormat value={el.Amount} suffix="B" />
              </p>
            </div>,
          ]
        : []),
    ];
    if (!el.Service && !isMobile) {
      return [...cell];
    }
    return cell;
  };

  const getPaymentBonusesRow = (el: BonusesItem): TableBodyCell => {
    return {
      id: el.id,
      className: 'payment-row',
      bodyCells: getPaymentBonusesCells(el),
    };
  };

  const getPaymentHistoryCells = (el: Payment) => {
    const cell = [
      <MobileCell
        element={el}
        subAccount={subAccount || userInfoState?.id}
        accountInfo={accountInfo?.Account}
        locale={fnsLocale}
      />,
      ...(!isMobile
        ? [
            <h5 className="light">
              {new DateVOv2({ format: 'yyyy-MM-dd HH:mm:ss', dateString: el.PayDate }).getDateString(locale)}
            </h5>,
          ]
        : []),
      ...(!isMobile ? [<PaymentHistoryDetails element={el} />] : []),
    ];
    if (!el.Service && !isMobile) {
      return [...cell];
    }
    return cell;
  };

  const getClassFromTypeId = (typeId: number) => {
    const mappedClasses = {
      1: 'payment-row top-up-balance',
      2: 'payment-row',
      3: 'payment-row',
      4: 'payment-row top-up-balance',
    };

    return mappedClasses?.[typeId];
  };

  const getPaymentHistoryRow = (el: Payment): TableBodyCell => {
    return {
      id: el.id,
      className: getClassFromTypeId(el.TypeID),
      bodyCells: getPaymentHistoryCells(el),
    };
  };

  const payments: TableBodyCell[] | undefined =
    paymentHistoryApi &&
    paymentHistoryApi.list.map((el) => {
      return getPaymentHistoryRow(el);
    });

  const bonuses: TableBodyCell[] | undefined =
    paymentBonusesApi &&
    paymentBonusesApi.list.map((el) => {
      return getPaymentBonusesRow(el);
    });

  const handlePaymentsYearSelect = (year: string) => {
    history.replace({
      pathname: GlobalRouteNames.PaymentHistory,
      search: `?period=${year}`,
    });
  };

  const handleBonusesYearSelect = (year: string) => {
    setBonusesQueryParams({ ...bonusesQueryParams, period: year });
    setBonusesYearsOptions(year);
    setBonusesPage(0);
  };

  const handleServiceTypeSelect = (type: AccountTypes | 'all services') => {
    if (type === 'all services') {
      setPaymentsQueryParams({ ...paymentsQueryParams, serviceType: '' });
    } else {
      setPaymentsQueryParams({ ...paymentsQueryParams, serviceType: type });
    }
    setServiceType(type);
  };

  const handleSubAccountSelect = (id: number) => {
    setPaymentsQueryParams({ ...paymentsQueryParams, subAccountId: id });
    setSubAccount(id);
  };

  useEffect(() => {
    const urlParams = new URLSearchParams(search);
    const paymentPeriod = getPaymentsPeriod(urlParams);
    if (paymentPeriod !== paymentsYearsOptions) {
      setPaymentsQueryParams({ ...paymentsQueryParams, period: paymentPeriod ?? `${currentYear}` });
      setPaymentsYearsOptions(paymentPeriod ?? `${currentYear}`);
      setPaymentsPage(0);
    }
  }, [search, isUserInfoDownloaded]);

  useEffect(() => {
    if (isError && error) {
      const message = getErrorMessage(error);
      customEnqueueSnackbar(message, 'error');
    }
  }, [isError, error]);

  const getPaymentHistoryContent = () => {
    if (isLoading) {
      return (
        <CustomSkeletonElement
          skeletonContainerClassName="skeleton-container"
          skeletonChildrenClassName="skeleton-children skeleton"
          childrenCount={4}
        />
      );
    }
    if (!isLoading && paymentHistoryApi && paymentHistoryApi.list.length) {
      return (
        <PaginationTable
          isAutoResizeEmptyPaginationRows={false}
          tableHead={isMobile ? mobileTableHead : tableHead}
          tableBodyCells={payments}
          isCollapsibleButton={!isMobile}
          isVerticalAlignCells={isMobile}
          currentPage={paymentsPage}
          setCurrentPage={setPaymentsPage}
          toTop={scrollToTop}
        />
      );
    }
    if (isError) {
      return <p>{t('paymentHistory.errorOccurred')}</p>;
    }
    return <EmptyNotification message={t('paymentHistory.noPayments')} />;
  };

  const getPaymentBonusesContent = () => {
    if (isLoadingBonuses) {
      return (
        <CustomSkeletonElement
          skeletonContainerClassName="skeleton-container"
          skeletonChildrenClassName="skeleton-children skeleton"
          childrenCount={4}
        />
      );
    }
    if (!isLoadingBonuses && paymentBonusesApi && paymentBonusesApi.list.length) {
      return (
        <PaginationTable
          isAutoResizeEmptyPaginationRows={false}
          tableHead={isMobile ? mobileTableHead : tableHead}
          tableBodyCells={bonuses}
          isCollapsibleButton={!isMobile}
          isVerticalAlignCells={isMobile}
          currentPage={bonusesPage}
          setCurrentPage={setBonusesPage}
          toTop={scrollToTop}
        />
      );
    }
    if (isBonusError) {
      return <p>{t('paymentHistory.errorOccurred')}</p>;
    }
    return <p>{t('paymentHistory.noBonuses')}</p>;
  };

  useEffect(() => {
    if (isBonusError && bonusesError) {
      const message = getErrorMessage(bonusesError);
      customEnqueueSnackbar(message, 'error');
    }
  }, [isBonusError, bonusesError]);

  return (
    <Switch>
      <Route path={GlobalRouteNames.PaymentHistory} exact>
        <div className="payment-history-page page">
          <div className="payment-history-page__container container">
            {!isTabsAvailable ? (
              <>
                <h1 ref={scrollToTop}>{t('paymentHistory.yourPayments')}</h1>
                <div className="payment-history-page__content">
                  <div className="payment-history-page__content__selects">
                    <YearSelect
                      yearsOptions={paymentsYearsOptions}
                      handleYearSelect={handlePaymentsYearSelect}
                      optionText="paymentHistory.paymentsFor"
                      startDate={userInfoState?.CreateDate || ''}
                    />
                    {isUserInfoDownloaded && accountInfo?.Account?.Role === 'admin' && (
                      <AccountAddressSelect
                        subAccount={subAccount || userInfoState.id}
                        optionsList={accountInfo.List}
                        handleSubAccountSelect={handleSubAccountSelect}
                      />
                    )}
                    {isAccountTypeSelector && (
                      <AccountTypeSelect accountType={serviceType} handleAccountTypeSelect={handleServiceTypeSelect} />
                    )}
                  </div>
                  {getPaymentHistoryContent()}
                </div>
              </>
            ) : (
              <>
                <div className="payment-history-page__tabs" ref={scrollToTop}>
                  <CustomTab
                    tabValue={tabValue}
                    setTabValue={handleSetTabValue}
                    orientation="horizontal"
                    tabs={paymentHistoryTabs.map((el) => {
                      return { label: t(el.label), id: el.id };
                    })}
                  />
                </div>
                <TabPanel value={tabValue} index={0}>
                  <div className="payment-history-page__content">
                    <div className="payment-history-page__content__selects">
                      <YearSelect
                        yearsOptions={paymentsYearsOptions}
                        handleYearSelect={handlePaymentsYearSelect}
                        optionText="paymentHistory.paymentsFor"
                        startDate={userInfoState?.CreateDate || ''}
                      />
                      {isUserInfoDownloaded && accountInfo?.Account?.Role === 'admin' && (
                        <AccountAddressSelect
                          subAccount={subAccount || userInfoState.id}
                          optionsList={accountInfo.List}
                          handleSubAccountSelect={handleSubAccountSelect}
                        />
                      )}
                      {isAccountTypeSelector && (
                        <AccountTypeSelect
                          accountType={serviceType}
                          handleAccountTypeSelect={handleServiceTypeSelect}
                        />
                      )}
                    </div>
                    {getPaymentHistoryContent()}
                  </div>
                </TabPanel>
                <TabPanel value={tabValue} index={1}>
                  <div className="payment-history-page__content">
                    <div className="payment-history-page__content__selects bonuses-for">
                      <YearSelect
                        yearsOptions={bonusesYearsOptions}
                        handleYearSelect={handleBonusesYearSelect}
                        optionText="paymentHistory.bonusesFor"
                        startDate={userInfoState?.CreateDate || ''}
                      />
                      {!!paymentBonusesApi?.expireDate && userInfoState?.Bonus && userInfoState?.Bonus > 0 ? (
                        <div className="payment-history-page__content__expire-bonuses">
                          {t('serviceManagement.bonusesExpireDate')} {expireDate}
                        </div>
                      ) : null}
                    </div>
                    {getPaymentBonusesContent()}
                  </div>
                </TabPanel>
              </>
            )}
          </div>
        </div>
      </Route>
      <Route component={Page404} path={GlobalRouteNames.Page404} />
    </Switch>
  );
};
